import { useState } from 'react';
import { useBusinessStore } from '../store/useBusinessStore';
import { useAuthStore } from '../store/useAuthStore';
import { supabase } from '../supabaseClient';
import toast from 'react-hot-toast';
import { joinStaffCities, formatStaffCities } from '../data/colombiaCities';

export const useStaff = () => {
  const { masterDb, activeBusinessId, setMasterDb } = useBusinessStore();
  const { userRole } = useAuthStore();
  const isDemo = ['9001', '9002', '9003'].includes(String(activeBusinessId));
  const isReadOnlyDemo = isDemo && userRole !== 'master';

  const activeBusiness = Array.isArray(masterDb) ? masterDb.find(b => String(b.id) === String(activeBusinessId)) : null;
  const staffList = Array.isArray(activeBusiness?.staff) ? activeBusiness.staff : [];

  const [newStaff, setNewStaff] = useState({
    name: '', specialty: '', google_calendar_id: '', telefono: '', pin_code: '', ciudad: '', comision_porcentaje: 0, available: true
  });

  const showNotification = (msg, type = 'success') => {
    if (type === 'error') toast.error(msg);
    else toast.success(msg);
  };

  const handleAddStaff = async () => {
    if (isReadOnlyDemo) {
      showNotification('Modo solo lectura: Las cuentas demo no se pueden modificar.', 'error');
      return;
    }

    if (activeBusiness) {
      const limite = activeBusiness.limite_profesionales || 2;
      if (staffList.length >= limite) {
        showNotification(`Límite alcanzado. Tu plan permite máximo ${limite} profesional(es). Mejora tu plan para añadir más.`, "error");
        return;
      }
    }
    
    if (!newStaff.name?.trim() || !newStaff.specialty?.trim()) {
      showNotification("Por favor completa el nombre y especialidad del staff", "error");
      return;
    }

    // La ciudad (reparto por ciudad) solo se manda si se eligió: la columna `staff.ciudad` la crea la migración 20261007d.
    const { ciudad, ...rest } = newStaff;
    const member = { ...rest, ...(joinStaffCities(ciudad) ? { ciudad: joinStaffCities(ciudad) } : {}), id: Date.now().toString(), business_id: activeBusinessId };
    
    try {
      const { error } = await supabase.from('staff').insert([member]);
      if (error) throw error;

      setMasterDb((Array.isArray(masterDb) ? masterDb : []).map(b => {
        if (String(b.id) === String(activeBusinessId)) {
          const currentStaff = Array.isArray(b.staff) ? b.staff : [];
          return { ...b, staff: [...currentStaff, member] };
        }
        return b;
      }));

      setNewStaff({ name: '', specialty: '', google_calendar_id: '', telefono: '', pin_code: '', ciudad: '', comision_porcentaje: 0, available: true });
      showNotification("Especialista añadido", "success");
    } catch (error) {
      console.error('Error adding staff:', error);
      showNotification("Error al guardar especialista", "error");
    }
  };

  const handleDeleteStaff = async (staffId) => {
    if (isReadOnlyDemo) {
      toast.error('Acción denegada: No se puede eliminar personal en las cuentas de demostración para visitantes.');
      return;
    }

    // Optimistic UI update
    const previousStaff = [...staffList];
    setMasterDb((Array.isArray(masterDb) ? masterDb : []).map(b => {
      if (String(b.id) === String(activeBusinessId)) {
        return {
          ...b,
          staff: (Array.isArray(b.staff) ? b.staff : []).filter(s => String(s?.id) !== String(staffId))
        };
      }
      return b;
    }));

    try {
      const { error } = await supabase.from('staff').delete().eq('id', staffId);
      if (error) throw error;
      showNotification("Especialista eliminado", "success");
    } catch (error) {
      console.error('Error deleting staff:', error);
      showNotification("Error al eliminar especialista", "error");
      
      // Rollback
      setMasterDb((Array.isArray(masterDb) ? masterDb : []).map(b => {
        if (String(b.id) === String(activeBusinessId)) {
          return { ...b, staff: previousStaff };
        }
        return b;
      }));
    }
  };

  const toggleStaffStatus = async (staffId) => {
    if (isReadOnlyDemo) {
      showNotification('Modo solo lectura: Las cuentas demo no se pueden modificar.', 'error');
      return;
    }

    const memberToUpdate = staffList.find(s => String(s?.id) === String(staffId));
    if (!memberToUpdate) return;
    
    const newStatus = !memberToUpdate.available;

    // Optimistic UI update
    setMasterDb((Array.isArray(masterDb) ? masterDb : []).map(b => {
      if (String(b.id) === String(activeBusinessId)) {
        return {
          ...b,
          staff: (Array.isArray(b.staff) ? b.staff : []).map(s => String(s?.id) === String(staffId) ? { ...s, available: newStatus } : s)
        };
      }
      return b;
    }));

    try {
      const { error } = await supabase.from('staff').update({ available: newStatus }).eq('id', staffId);
      if (error) throw error;
    } catch (error) {
      console.error('Error toggling staff status:', error);
      showNotification("Error al actualizar estado", "error");
      
      // Rollback
      setMasterDb((Array.isArray(masterDb) ? masterDb : []).map(b => {
        if (String(b.id) === String(activeBusinessId)) {
          return {
            ...b,
            staff: (Array.isArray(b.staff) ? b.staff : []).map(s => String(s?.id) === String(staffId) ? { ...s, available: !newStatus } : s)
          };
        }
        return b;
      }));
    }
  };

  // Ciudades de reparto de un domiciliario (una o varias, guardadas separadas por «|»): en el portal /delivery solo ve los pedidos de esas ciudades (vacías = ve todos).
  const setStaffCity = async (staffId, ciudad) => {
    if (isReadOnlyDemo) {
      showNotification('Modo solo lectura: Las cuentas demo no se pueden modificar.', 'error');
      return;
    }
    const value = joinStaffCities(ciudad); // mismos nombres que la lista de ciudades, sin variantes ni repetidos
    const previous = staffList.find(s => String(s?.id) === String(staffId))?.ciudad ?? null;
    const apply = (city) => setMasterDb((Array.isArray(masterDb) ? masterDb : []).map(b => (
      String(b.id) === String(activeBusinessId)
        ? { ...b, staff: (Array.isArray(b.staff) ? b.staff : []).map(s => String(s?.id) === String(staffId) ? { ...s, ciudad: city } : s) }
        : b
    )));
    apply(value || null);
    try {
      const { error } = await supabase.from('staff').update({ ciudad: value || null }).eq('id', staffId);
      if (error) throw error;
      showNotification(value ? `Ciudades: ${formatStaffCities(value)}` : 'Sin ciudades: verá los pedidos de todas', 'success');
    } catch (error) {
      console.error('Error saving staff city:', error);
      apply(previous);
      showNotification(/ciudad/i.test(error?.message || '') ? 'Falta ejecutar la migración 20261007d_public_orders_cities.sql en Supabase.' : 'Error al guardar la ciudad', 'error');
    }
  };

  const handleEditStaff = async (member) => {
    if (isReadOnlyDemo) {
      showNotification('Modo solo lectura: Las cuentas demo no se pueden modificar.', 'error');
      return;
    }

    if (!member) return;
    const newName = window.prompt("Editar nombre:", member.name);
    if (!newName || newName === member.name) return;

    // Optimistic UI update
    setMasterDb((Array.isArray(masterDb) ? masterDb : []).map(b => {
      if (String(b.id) === String(activeBusinessId)) {
        return {
          ...b,
          staff: (Array.isArray(b.staff) ? b.staff : []).map(s => String(s?.id) === String(member.id) ? { ...s, name: newName } : s)
        };
      }
      return b;
    }));

    try {
      const { error } = await supabase.from('staff').update({ name: newName }).eq('id', member.id);
      if (error) throw error;
      showNotification("Nombre actualizado", "success");
    } catch (error) {
      console.error('Error editing staff:', error);
      showNotification("Error al actualizar nombre", "error");
      
      // Rollback
      setMasterDb((Array.isArray(masterDb) ? masterDb : []).map(b => {
        if (String(b.id) === String(activeBusinessId)) {
          return {
            ...b,
            staff: (Array.isArray(b.staff) ? b.staff : []).map(s => String(s?.id) === String(member.id) ? { ...s, name: member.name } : s)
          };
        }
        return b;
      }));
    }
  };

  return {
    activeBusiness,
    staff: staffList,
    newStaff,
    setNewStaff,
    handleAddStaff,
    handleDeleteStaff,
    toggleStaffStatus,
    handleEditStaff,
    setStaffCity
  };
};
